import { useEffect, useState } from 'react';

interface ShareModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  url: string;
}

export default function ShareModal({ open, onClose, title, url }: ShareModalProps) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) setCopied(false);
  }, [open]);

  if (!open) return null;

  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=8&data=${encodedUrl}`;

  const shareLinks = [
    { label: '微博', icon: 'ri-weibo-fill', href: `https://service.weibo.com/share/share.php?url=${encodedUrl}&title=${encodedTitle}` },
    { label: 'QQ空间', icon: 'ri-qq-fill', href: `https://sns.qzone.qq.com/cgi-bin/qzshare/cgi_qzshare_onekey?url=${encodedUrl}&title=${encodedTitle}` },
    { label: 'X', icon: 'ri-twitter-x-fill', href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}` },
  ];

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <button type="button" aria-label="关闭分享" onClick={onClose} className="absolute inset-0 cursor-pointer bg-foreground-950/50" />
      <div className="gs-fade-up relative z-10 w-full max-w-md rounded-xl border border-background-200 bg-background-50 p-6">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-heading text-lg font-bold text-foreground-950">分享内容</h3>
            <p className="mt-1 line-clamp-2 text-xs text-foreground-500">{title}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="关闭"
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100 hover:text-foreground-800"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="mt-5 flex justify-center">
          <div className="h-[180px] w-[180px] overflow-hidden rounded-lg border border-background-200 bg-background-50">
            <img src={qrSrc} alt={`${title} 分享二维码`} className="h-full w-full object-contain" width={180} height={180} />
          </div>
        </div>
        <p className="mt-2 text-center text-xs text-foreground-400">扫码在手机上打开</p>

        <div className="mt-5 flex items-center gap-2">
          <input
            readOnly
            value={url}
            className="h-10 flex-1 truncate rounded-md border border-background-300 bg-background-100 px-3 text-xs text-foreground-600"
          />
          <button
            type="button"
            onClick={handleCopy}
            className={`inline-flex h-10 shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md px-3.5 text-sm font-label transition-colors ${
              copied ? 'bg-accent-500 text-background-50' : 'bg-primary-500 text-background-50 hover:bg-primary-600 dark:text-foreground-950'
            }`}
          >
            <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'}></i>
            {copied ? '已复制' : '复制链接'}
          </button>
        </div>

        <div className="mt-5 flex justify-center gap-3">
          {shareLinks.map((item) => (
            <a
              key={item.label}
              href={item.href}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="flex flex-col items-center gap-1.5"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-secondary-100 text-secondary-900 transition-colors hover:bg-primary-100 hover:text-primary-700">
                <i className={`${item.icon} text-xl`}></i>
              </span>
              <span className="text-[11px] text-foreground-500">{item.label}</span>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}